import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { CustomRoute, DailyRoute } from '../interfaces/custom-route.interface';
import { CustomRouteResponse, CustomRouteResponseCompleted, DailyRouteResponse } from '../interfaces/custom-route.response';
import { customRouteAdapter } from '../adapters/custom-route.adapter';
import { HttpClient } from '@angular/common/http';
import { RestService } from 'src/app/services/rest.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { PlannerDayStateService } from '../shared/planner/services';
import { UpdateCustomEditedRouteBody } from '../shared/planner/components/edit-route/interfaces/edit-route.interface';
import { CustomEditedRouteResponse } from '../shared/planner/components/edit-route/interfaces/edit-route.response';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CustomRouteService {
  // Service injections
  private _http = inject(HttpClient);
  private _restService = inject(RestService);
  private _sessionStorageService = inject(SessionStorageService);
  private _plannerDayStateService = inject(PlannerDayStateService);
  
  // State service signals
  private _accumulatedDailyRoutes = signal<DailyRouteResponse[]>([]);
  public accumulatedDailyRoutes = this._accumulatedDailyRoutes.asReadonly();
  
  private _customRouteData = signal<CustomRoute | null>(null);
  public customRouteData = this._customRouteData.asReadonly();
  
  private _token = signal<string | null>(null);
  public token = this._token.asReadonly();
  
  private _currentOrder = signal<number>(0); // Order of the custom route for calling the next daily route from API
  public currentOrder = this._currentOrder.asReadonly();
  
  private _isLoading = signal<boolean>(false);
  public isLoading = this._isLoading.asReadonly();
  
  private _routeUpdated = signal<boolean>(false);
  public routeUpdated = this._routeUpdated.asReadonly();
  
  private _isWelcomeViewShown = signal<boolean>(false);
  public isWelcomeViewShown = this._isWelcomeViewShown.asReadonly();
  
  // Local variables
  private _routeId: number | null = null;
  
  /**
   * Setters
   */
  setCustomRouteData(customRoute: CustomRoute): void {
    this._customRouteData.set(customRoute);
  }
  
  setToken(token: string): void {
    this._token.set(token);
  }
  
  setCurrentOrder(order: number): void {
    this._currentOrder.set(order);
  }
  
  setIsLoading(isLoading: boolean): void {
    this._isLoading.set(isLoading);
  }
  
  setRouteUpdated(isUpdated: boolean): void {
    this._routeUpdated.set(isUpdated);
  }
  
  setIsWelcomeViewShown(isShown: boolean): void {
    this._isWelcomeViewShown.set(isShown);
  }

  /**
   * Http requests
   */
  launchRoutePlanning(token: string, order: number): Observable<CustomRouteResponse> {
    const url = `${this._restService.apiUrlBase}/api/launch-job-route-planning`;
    const body = {
      token: token,
      order: order
    }
    
    return this._http.post<CustomRouteResponse>(
      url,
      body,
      { observe: 'response' }  // include status, headers, etc.
    ).pipe(
      map((httpResponse) => {
        const status = httpResponse.status;
        const responseBody = httpResponse.body;

        console.log("HTTP Status:", status);
        console.log("Response Body:", responseBody);

        // Combina status y body en un solo objeto
        return {
          status,
          ...(responseBody as object)
        } as CustomRouteResponse;
      }),
      catchError(error => {
        console.error(`Error fetching custom route data: ${error}`);
        return throwError(() => new Error('HTTP request failed', { cause: error })); // Propagate the error
      })
    );
  }
  
  getCustomRouteResponse(token: string, order?: number): Observable<CustomRouteResponse> {
    const url = `${this._restService.apiUrlBase}/api/get-route-completed`;
    const body = {
      token: token,
      order: order
    }
    
    return this._http.post<CustomRouteResponse>(
      url,
      body,
      { observe: 'response' }  // include status, headers, etc.
    ).pipe(
      map((httpResponse) => {
        const status = httpResponse.status;
        const responseBody = httpResponse.body;

        console.log("HTTP Status:", status);
        console.log("Response Body:", responseBody);

        // Combina status y body en un solo objeto
        return {
          status,
          ...(responseBody as object)
        } as CustomRouteResponse;
      }),
      catchError(error => {
        console.error(`Error fetching custom route data: ${error}`);
        return throwError(() => new Error('HTTP request failed', { cause: error })); // Propagate the error
      })
    );
  }
  
  updateCustomRouteData(body: UpdateCustomEditedRouteBody): Observable<CustomEditedRouteResponse> {
    this._isLoading.set(true);
    
    const url = `${this._restService.apiUrlBase}/api/modify-route-planning`;
    
    return this._http.post<CustomRouteResponseCompleted>(url, body,)
      .pipe(
        map((response: CustomEditedRouteResponse) => {
          this._isLoading.set(false);
          
          console.log('Custom route data updated successfully:', response);
          return response;
        }),
        catchError(error => {
          this._isLoading.set(false);
          console.error(`Error updating custom route data: ${error}`);
          return throwError(() => new Error('HTTP request failed', { cause: error })); // Propagate the error
        })
      )
  }
  
  /**
   * Adds a completed custom route to the service state customRouteData.
   */
  addDailyRoute(response: CustomRouteResponseCompleted): void {
    if (this._routeId === null) {
      this._routeId = response.id;
    }
  
    this._accumulatedDailyRoutes.update(prev => [...prev, response.dailyRoutes]);

    const updatedCustomRoute: CustomRoute = customRouteAdapter(
      this._routeId,
      this._accumulatedDailyRoutes(),
      response.token ?? '',
      response.language ?? 'es',
      response.stats
    );
    
    this.setCustomRouteData(updatedCustomRoute);

    if (!environment.production) {
      console.log('Updated Custom Route:', updatedCustomRoute);
    }
  }
  
  /**
   * Update a daily route in the customRouteData with a new edited daily route.
   */
  updateDailyRoute(response: CustomRouteResponseCompleted, dayToShowIndex: number): void {
    if (this._routeId === null) {
      this._routeId = response.id;
    }
    
    // adapt the response to update the daily route data
    const updatedDailyRoute: DailyRoute = customRouteAdapter(
      this._routeId, 
      [response.dailyRoutes],
      response.token ?? '',
      response.language ?? 'es',
      response.stats
    ).dailyRoutes[0];
    
    // replace the daily route at the specified index
    const updatedCustomRoute: CustomRoute = {
      ...this._customRouteData(),
      dailyRoutes: this._customRouteData().dailyRoutes.map((dailyRoute, index) =>
        index === dayToShowIndex ? updatedDailyRoute : dailyRoute
      )
    };
    
    this.setCustomRouteData(updatedCustomRoute);
    this._routeUpdated.set(true);

    if (!environment.production) {
      console.log('Custom Route Data after updating:', updatedCustomRoute);
    }
  }

  /**
   * Resets
   */
  resetRouteUpdate(){
    this._routeUpdated.set(false);
  }

  /**
   * Resets the service state, clearing accumulated daily routes,
   * custom route data, token, and current order.
   * It is called every time you go to register-user.component.ts
   */
  reset() {
    this._accumulatedDailyRoutes.set([]);
    this.setCustomRouteData(null);
    this.setToken(null);
    this.setCurrentOrder(0);
    this._routeUpdated.set(false);
    this._plannerDayStateService.resetDayToShowIndex();
    this._routeId = null;
  }
}
